import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import ApiService from '../../../../../services/ApiService';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../../components/StandardPage';
import { OverflowCell } from '../../../../../components/OverflowCell';

const taxScenarioScreenKey = 'tax_scenario';

const StyledSelect = styled.select`
  width: min(100%, 9.5rem);
  height: 1.75rem;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  padding: 0 0.8rem;
  background-color: #ebebeb;
  color: #111827;
  font-size: 0.86rem;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: #93c5fd;
    box-shadow: 0 0 0 2px rgba(147, 197, 253, 0.35);
  }
`;

const getSelectedScenario = (
  taxConfig: any,
  selectedScenarioIds: Record<number, number>,
) => {
  const scenarios = taxConfig.TaxScenario || [];
  if (scenarios.length === 0) return null;
  const selectedId = taxConfig.id ? selectedScenarioIds[taxConfig.id] : undefined;
  return (
    scenarios.find((s: any) => s.id === selectedId) ||
    scenarios[0] ||
    null
  );
};

const formatNcmsSummary = (row: any): string => {
  const classifiers = row.TaxClassifier || [];
  const ncms = classifiers
    .map((c: any) => c.Ncm?.ncm || c.Nbs?.nbs)
    .filter(Boolean);
  if (ncms.length === 0) return '—';
  return ncms.join(', ');
};

const formatScenarioStates = (scenario: any): string => {
  if (!scenario) return '—';
  const states = scenario.TaxScenarioState || [];
  if (states.length === 0) return 'Todos os estados';
  const list = states
    .map((s: any) => s.State?.uf || s.State?.name || s.region)
    .filter(Boolean);
  return list.length > 0 ? list.join(', ') : 'Todos os estados';
};

const ScenarioTable: React.FC = () => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { refresh, scenarioFilters, search } = useOutletContext<{ refresh?: boolean; scenarioFilters?: any[]; search?: string }>() ?? {};
  const [scenarios, setScenarios] = useState<any[]>([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<Record<number, number>>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleScenarioChange = useCallback((taxConfigId: number, scenarioId: number) => {
    setSelectedScenarioIds(prev => ({ ...prev, [taxConfigId]: scenarioId }));
  }, []);

  const handleDelete = async (id: number, nome: string) => {
    if (!window.confirm(`Excluir o cenário "${nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await ApiService.fiscal.deleteTaxConfig(id);
      setScenarios(prev => prev.filter(s => s.id !== id));
    } catch (error: any) {
      const msg = error?.response?.data?.message;
      setFeedback({
        message: Array.isArray(msg) ? msg.join('; ') : msg || 'Erro ao excluir cenário.',
        isOpen: true,
        statusOk: false,
        semTitulo: error?.response?.status === 409,
      });
    }
  };

  const fetchScenarios = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: { filters?: string; search?: string } = {};
      if (search && search.trim() !== '') {
        params.search = search;
      }
      if (scenarioFilters && scenarioFilters.length > 0) {
        params.filters = JSON.stringify(scenarioFilters);
      }
      const response = await ApiService.fiscal.getTaxConfigs(params);
      const data = Array.isArray(response.data) ? response.data : response.data.data;
      setScenarios(data || []);
    } catch (error) {
      console.error('Erro ao buscar cenários:', error);
    } finally {
      setIsLoading(false);
    }
  }, [scenarioFilters, search]);

  useEffect(() => { fetchScenarios(); }, [fetchScenarios, refresh]);

  const columns = useMemo<StandardColumn<any>[]>(() => [
    {
      id: 'nome',
      label: 'Nome',
      accessor: (row) => row.nome || 'Sem nome',
      cell: (row) => <OverflowCell text={row.nome || 'Sem nome'} maxWidth={180} />,
    },
    {
      id: 'cnpjOrigem',
      label: 'CNPJ de origem',
      sortable: false,
      accessor: (row) => {
        const cnpj = row.CompanyEmissor?.cnpj;
        if (!cnpj) return '—';
        const tipo = row.CompanyEmissor?.tipoEstabelecimento || (cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial');
        return `${cnpj} — ${tipo}`;
      },
      cell: (row) => {
        const cnpj = row.CompanyEmissor?.cnpj;
        if (!cnpj) return <span>—</span>;
        const tipo = row.CompanyEmissor?.tipoEstabelecimento || (cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial');
        return <OverflowCell text={`${cnpj} — ${tipo}`} maxWidth={170} />;
      },
    },
    {
      id: 'cfop',
      label: 'CFOP',
      accessor: (row) => row.Cfop?.code ? String(row.Cfop.code) : '—',
      cell: (row) => <strong style={{ color: '#0085CC' }}>{row.Cfop?.code || '—'}</strong>,
    },
    {
      id: 'ncms',
      label: 'NCMs',
      accessor: (row) => formatNcmsSummary(row),
      cell: (row) => <OverflowCell text={formatNcmsSummary(row)} maxWidth={180} />,
    },
    {
      id: 'cstPIS_COFINS',
      label: 'CST PIS/COFINS',
      accessor: (row) => row.cstPIS_COFINS || '—',
      cell: (row) => <OverflowCell text={row.cstPIS_COFINS || '—'} maxWidth={130} />,
    },
    {
      id: 'aliquotaPIS',
      label: 'Alíquota PIS%',
      accessor: (row) => (row.aliquotaPIS != null ? `${row.aliquotaPIS}%` : '—'),
    },
    {
      id: 'aliquotaCOFINS',
      label: 'Alíquota COFINS%',
      accessor: (row) => (row.aliquotaCOFINS != null ? `${row.aliquotaCOFINS}%` : '—'),
    },
    {
      id: 'cstIPI',
      label: 'CST IPI',
      accessor: (row) => row.cstIPI || '—',
      cell: (row) => <OverflowCell text={row.cstIPI || '—'} maxWidth={130} />,
    },
    {
      id: 'cenario',
      label: 'Cenário',
      sortable: false,
      disableBulkEdit: true,
      accessor: (row) => {
        const list = row.TaxScenario || [];
        const selected = getSelectedScenario(row, selectedScenarioIds);
        const idx = list.findIndex((s: any) => s.id === selected?.id);
        return idx >= 0 ? `Cenário ${idx + 1}` : 'Nenhum';
      },
      cell: (row) => {
        const list = row.TaxScenario || [];
        const selected = getSelectedScenario(row, selectedScenarioIds);
        if (list.length > 1) {
          return (
            <StyledSelect
              value={selected?.id || ''}
              onChange={(e) => {
                if (row.id) handleScenarioChange(row.id, Number(e.target.value));
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {list.map((sc: any, idx: number) => (
                <option key={sc.id || idx} value={sc.id}>
                  {`Cenário ${idx + 1}`}
                </option>
              ))}
            </StyledSelect>
          );
        }
        return list.length === 1 ? 'Cenário 1' : 'Nenhum';
      },
    },
    {
      id: 'cstICMS',
      label: 'CST ICMS',
      accessor: (row) => getSelectedScenario(row, selectedScenarioIds)?.cstICMS || '—',
      cell: (row) => <OverflowCell text={getSelectedScenario(row, selectedScenarioIds)?.cstICMS || '—'} maxWidth={130} />,
    },
    {
      id: 'aliquotaICMS',
      label: 'Alíquota ICMS (%)',
      accessor: (row) => {
        const s = getSelectedScenario(row, selectedScenarioIds);
        return s?.aliquotaICMS != null ? `${s.aliquotaICMS}%` : '—';
      },
    },
    {
      id: 'baseDeCalculo',
      label: 'Base de cálculo (%)',
      accessor: (row) => {
        const s = getSelectedScenario(row, selectedScenarioIds);
        return s?.baseDeCalculo != null ? `${s.baseDeCalculo}%` : '—';
      },
    },
    {
      id: 'diferencialAliquota',
      label: 'Diferencial de alíquota',
      accessor: (row) => {
        const s = getSelectedScenario(row, selectedScenarioIds);
        return s ? (s.diferencialAliquota ? 'Sim' : 'Não') : '—';
      },
    },
    {
      id: 'cstIBS_CBS',
      label: 'CST IBS/CBS',
      accessor: (row) => getSelectedScenario(row, selectedScenarioIds)?.cstIBS_CBS || '—',
      cell: (row) => <OverflowCell text={getSelectedScenario(row, selectedScenarioIds)?.cstIBS_CBS || '—'} maxWidth={130} />,
    },
    {
      id: 'classificacaoTributaria',
      label: 'Classificação tributária (cClassTrib)',
      accessor: (row) => getSelectedScenario(row, selectedScenarioIds)?.classificacaoTributaria || '—',
      cell: (row) => <OverflowCell text={getSelectedScenario(row, selectedScenarioIds)?.classificacaoTributaria || '—'} maxWidth={180} />,
    },
    {
      id: 'aliquotaCBS',
      label: 'Alíquota CBS (%)',
      accessor: (row) => {
        const s = getSelectedScenario(row, selectedScenarioIds);
        return s?.aliquotaCBS != null ? `${s.aliquotaCBS}%` : '—';
      },
    },
    {
      id: 'aliquotaIBS',
      label: 'Alíquota IBS (%)',
      accessor: (row) => {
        const s = getSelectedScenario(row, selectedScenarioIds);
        return s?.aliquotaIBS != null ? `${s.aliquotaIBS}%` : '—';
      },
    },
    {
      id: 'aplicarA',
      label: 'Aplicar a:',
      accessor: (row) => formatScenarioStates(getSelectedScenario(row, selectedScenarioIds)),
      cell: (row) => <OverflowCell text={formatScenarioStates(getSelectedScenario(row, selectedScenarioIds))} maxWidth={180} />,
    },
  ], [selectedScenarioIds, handleScenarioChange]);

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(taxScenarioScreenKey, columns);

  return (
    <StandardDataTable
      tableName="TaxConfig"
      columns={columns}
      data={scenarios}
      columnsState={columnsState}
      onColumnsChange={handleColumnsChange}
      onRestoreColumns={handleRestoreColumns}
      isLoading={isLoading}
      countLabel='cenários'
      exportFileName='cenarios-fiscais'
      emptyMessage='Nenhum cenário encontrado.'
      rowActions={(row) => [
        { label: 'Editar', icon: <Pencil size={16} />, onClick: () => navigate(`/panel/invoice/tax-scenario/create?editId=${row.id}`) },
        { label: 'Excluir', icon: <Trash2 size={16} />, onClick: () => handleDelete(row.id, row.nome || 'sem nome') },
      ]}
    />
  );
};

export default ScenarioTable;
